'use client';

import React, { useMemo, useCallback } from 'react';
import { useDebounce } from 'use-debounce';
import { Checkbox, CircularProgress } from 'src/components';
import { useGetRegionAnalyticsQuery } from 'src/api/redux';
import { useAnalytics } from 'src/hooks/analytics';
import { REGION_HIERARCHY } from 'src/components/TerritoryManagement/components/TerritoryOptimizePanel/constants/regionHierarchy';

interface RegionGroup {
  region: MedScout.Region;
  divisions: MedScout.Region[];
}

const AnalyticsRegionPanel: React.FC = () => {
  const {
    geographySearch,
    tempSelectedGeographies,
    tempSelectedGeographyObjects,
    setTempSelectedGeographies,
    setTempSelectedGeographyObjects,
    toggleTempGeography,
  } = useAnalytics() || {};

  const [debouncedSearchQuery] = useDebounce(geographySearch, 300);

  // Fetch all regions (shared cache with AnalyticsStatePanel)
  const { data: allRegions, isFetching } = useGetRegionAnalyticsQuery({
    queryParams: {
      sort: 'name',
      order: 'asc',
    },
  });

  // Extract country-level items (e.g. USA, USA + Territories)
  const countryItems = useMemo(() => {
    if (!allRegions) return [];
    return allRegions.filter((r) => r.type === 'country');
  }, [allRegions]);

  // Build hierarchical groups from region + division items
  const regionGroups = useMemo<RegionGroup[]>(() => {
    if (!allRegions) return [];

    const regionByName = new Map<string, MedScout.Region>();
    allRegions.forEach((r) => regionByName.set(r.name, r));

    return REGION_HIERARCHY.map(({ regionName, divisionNames }) => {
      const region = regionByName.get(regionName);
      const divisions = divisionNames
        .map((name) => regionByName.get(name))
        .filter((r): r is MedScout.Region => r != null);

      if (!region) return null;
      return { region, divisions };
    }).filter((g): g is RegionGroup => g != null);
  }, [allRegions]);

  // Apply search filtering (preserving hierarchy)
  const filteredGroups = useMemo(() => {
    const query = debouncedSearchQuery?.toLowerCase().trim() || '';
    if (!query) return regionGroups;

    return regionGroups
      .map((group) => {
        const regionMatches = group.region.name.toLowerCase().includes(query);
        const matchingDivisions = group.divisions.filter((d) =>
          d.name.toLowerCase().includes(query)
        );
        if (regionMatches) return group;
        if (matchingDivisions.length > 0)
          return { ...group, divisions: matchingDivisions };
        return null;
      })
      .filter((g): g is RegionGroup => g != null);
  }, [regionGroups, debouncedSearchQuery]);

  // Filter country items by search
  const filteredCountryItems = useMemo(() => {
    const query = debouncedSearchQuery?.toLowerCase().trim() || '';
    if (!query) return countryItems;
    return countryItems.filter((c) => c.name.toLowerCase().includes(query));
  }, [countryItems, debouncedSearchQuery]);

  // All selectable items (countries + regions + divisions) for Select All
  const allItems = useMemo(
    () => [
      ...countryItems,
      ...regionGroups.flatMap((group) => [group.region, ...group.divisions]),
    ],
    [countryItems, regionGroups]
  );

  const selectedIdSet = useMemo(
    () => new Set(tempSelectedGeographies || []),
    [tempSelectedGeographies]
  );

  const isRegionFullySelected = useCallback(
    (group: RegionGroup) => {
      return (
        selectedIdSet.has(group.region.id) &&
        group.divisions.every((d) => selectedIdSet.has(d.id))
      );
    },
    [selectedIdSet]
  );

  const allSelected = useMemo(
    () =>
      allItems.length > 0 &&
      allItems.every((item) => selectedIdSet.has(item.id)),
    [allItems, selectedIdSet]
  );

  // Handle Select All
  const handleSelectAll = useCallback(() => {
    const allIds = allItems.map((item) => item.id);

    if (allSelected) {
      const idsToRemove = new Set(allIds);
      setTempSelectedGeographies?.(
        (tempSelectedGeographies || []).filter((id) => !idsToRemove.has(id))
      );
      setTempSelectedGeographyObjects?.(
        (tempSelectedGeographyObjects || []).filter(
          (obj) => !idsToRemove.has(obj.id)
        )
      );
    } else {
      const existingIds = new Set(tempSelectedGeographies || []);
      const newIds = allIds.filter((id) => !existingIds.has(id));
      const newObjects = allItems
        .filter((item) => !existingIds.has(item.id))
        .map((item) => ({
          type: 'region' as const,
          id: item.id,
          name: item.name,
        }));

      setTempSelectedGeographies?.([
        ...(tempSelectedGeographies || []),
        ...newIds,
      ]);
      setTempSelectedGeographyObjects?.([
        ...(tempSelectedGeographyObjects || []),
        ...newObjects,
      ]);
    }
  }, [
    allItems,
    allSelected,
    tempSelectedGeographies,
    tempSelectedGeographyObjects,
    setTempSelectedGeographies,
    setTempSelectedGeographyObjects,
  ]);

  // Handle toggling a parent region (cascade to children)
  const handleToggleRegion = useCallback(
    (group: RegionGroup) => {
      const groupItems = [group.region, ...group.divisions];
      const groupIds = groupItems.map((item) => item.id);
      const fullySelected = isRegionFullySelected(group);

      if (fullySelected) {
        const idsToRemove = new Set(groupIds);
        setTempSelectedGeographies?.(
          (tempSelectedGeographies || []).filter((id) => !idsToRemove.has(id))
        );
        setTempSelectedGeographyObjects?.(
          (tempSelectedGeographyObjects || []).filter(
            (obj) => !idsToRemove.has(obj.id)
          )
        );
      } else {
        const existingIds = new Set(tempSelectedGeographies || []);
        const newIds = groupIds.filter((id) => !existingIds.has(id));
        const newObjects = groupItems
          .filter((item) => !existingIds.has(item.id))
          .map((item) => ({
            type: 'region' as const,
            id: item.id,
            name: item.name,
          }));

        setTempSelectedGeographies?.([
          ...(tempSelectedGeographies || []),
          ...newIds,
        ]);
        setTempSelectedGeographyObjects?.([
          ...(tempSelectedGeographyObjects || []),
          ...newObjects,
        ]);
      }
    },
    [
      isRegionFullySelected,
      tempSelectedGeographies,
      tempSelectedGeographyObjects,
      setTempSelectedGeographies,
      setTempSelectedGeographyObjects,
    ]
  );

  // Handle toggling a division (cascade to parent)
  const handleToggleDivision = useCallback(
    (division: MedScout.Region, group: RegionGroup) => {
      const isSelected = selectedIdSet.has(division.id);

      if (isSelected) {
        // Uncheck division and also uncheck parent if it was checked
        const idsToRemove = new Set([division.id]);
        if (selectedIdSet.has(group.region.id)) {
          idsToRemove.add(group.region.id);
        }
        setTempSelectedGeographies?.(
          (tempSelectedGeographies || []).filter((id) => !idsToRemove.has(id))
        );
        setTempSelectedGeographyObjects?.(
          (tempSelectedGeographyObjects || []).filter(
            (obj) => !idsToRemove.has(obj.id)
          )
        );
      } else {
        // Check division
        const newIds = [...(tempSelectedGeographies || []), division.id];
        const newObjects = [
          ...(tempSelectedGeographyObjects || []),
          {
            type: 'region' as const,
            id: division.id,
            name: division.name,
          },
        ];

        // Auto-check parent if all siblings are now selected
        const allDivisionIds = group.divisions.map((d) => d.id);
        const newIdSet = new Set(newIds);
        if (
          allDivisionIds.every((id) => newIdSet.has(id)) &&
          !newIdSet.has(group.region.id)
        ) {
          newIds.push(group.region.id);
          newObjects.push({
            type: 'region' as const,
            id: group.region.id,
            name: group.region.name,
          });
        }

        setTempSelectedGeographies?.(newIds);
        setTempSelectedGeographyObjects?.(newObjects);
      }
    },
    [
      selectedIdSet,
      tempSelectedGeographies,
      tempSelectedGeographyObjects,
      setTempSelectedGeographies,
      setTempSelectedGeographyObjects,
    ]
  );

  const hasVisibleItems =
    filteredCountryItems.length > 0 || filteredGroups.length > 0;

  return (
    <div className="flex-1 overflow-y-auto" data-testid="analytics-region-panel">
      {hasVisibleItems && (
        <div className="w-full px-6 inline-flex flex-col justify-start items-start gap-4 py-4">
          {/* Select All */}
          <div
            onClick={handleSelectAll}
            className="inline-flex justify-start items-center gap-2 cursor-pointer"
            data-testid="analytics-region-select-all"
          >
            <Checkbox
              size="sm"
              checked={allSelected}
              onChange={handleSelectAll}
              aria-label="Select all regions"
            />
            <div className="justify-start text-secondary text-sm font-normal font-['Inter'] leading-5">
              Select All
            </div>
          </div>

          <div className="self-stretch h-0 border-b border-neutral-300" />

          {/* Country-level items (e.g. USA, USA + Territories) */}
          {filteredCountryItems.map((country) => (
            <div
              key={country.id}
              onClick={() =>
                toggleTempGeography?.(country.id, country.name, 'region')
              }
              className="inline-flex justify-start items-center gap-2 cursor-pointer"
              data-testid={`analytics-region-country-${country.id}`}
            >
              <Checkbox
                size="sm"
                checked={selectedIdSet.has(country.id)}
                onChange={() =>
                  toggleTempGeography?.(country.id, country.name, 'region')
                }
                aria-label={`Select ${country.name}`}
              />
              <div className="justify-start text-secondary text-sm font-semibold font-['Inter'] leading-5">
                {country.name}
              </div>
            </div>
          ))}

          {/* Hierarchical region/division list */}
          {filteredGroups.map((group) => (
            <div key={group.region.id} className="w-full flex flex-col gap-2">
              {/* Parent region row */}
              <div
                onClick={() => handleToggleRegion(group)}
                className="inline-flex justify-start items-center gap-2 cursor-pointer"
                data-testid={`analytics-region-group-${group.region.id}`}
              >
                <Checkbox
                  size="sm"
                  checked={isRegionFullySelected(group)}
                  onChange={() => handleToggleRegion(group)}
                  aria-label={`Select ${group.region.name}`}
                />
                <div className="justify-start text-secondary text-sm font-semibold font-['Inter'] leading-5">
                  {group.region.name}
                </div>
              </div>

              {/* Division rows (indented) */}
              {group.divisions.map((division) => (
                <div
                  key={division.id}
                  onClick={() => handleToggleDivision(division, group)}
                  className="pl-6 inline-flex justify-start items-center gap-2 cursor-pointer"
                  data-testid={`analytics-region-division-${division.id}`}
                >
                  <Checkbox
                    size="sm"
                    checked={selectedIdSet.has(division.id)}
                    onChange={() => handleToggleDivision(division, group)}
                    aria-label={`Select ${division.name}`}
                  />
                  <div className="justify-start text-secondary text-sm font-['Inter'] leading-5">
                    {division.name}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
      {isFetching && (
        <div className="h-full w-full flex items-center justify-center px-4 py-2 text-center text-xs text-neutral-500">
          <CircularProgress size="sm" color="neutral" />
        </div>
      )}
      {!isFetching && !hasVisibleItems && (
        <div className="h-full w-full flex items-center justify-center px-4 py-2 text-center text-xs text-neutral-500">
          No regions found
        </div>
      )}
    </div>
  );
};

export default AnalyticsRegionPanel;
